import { z } from 'zod';
import {
  AcademicYear,
  AdHocInvoiceId,
  FamilyId,
  ParentContactId,
  RecurringFeeAssignmentId,
  StudentId,
} from '../types/branded';
import { BillingCategorySchema } from '../types/enums';

export const SchoolTuitionMirrorCorrelationSchema = z.object({
  providerEventId: z.string().nullable(),
  eventType: z.string().nullable(),
  stripeEventOccurredAt: z.string().nullable(),
  paymentIntentId: z.string().nullable(),
  checkoutSessionId: z.string().nullable(),
  chargeId: z.string().nullable(),
  disputeId: z.string().nullable(),
});
export type SchoolTuitionMirrorCorrelation = z.infer<typeof SchoolTuitionMirrorCorrelationSchema>;

export const SchoolTuitionTransactionSchema = z.object({
  id: z.string().min(1),
  familyId: FamilyId.nullable(),
  studentId: StudentId.nullable(),
  academicYear: AcademicYear.nullable(),
  entryType: z.string().min(1),
  amountCents: z.number(),
  description: z.string().nullable(),
  referenceCode: z.string().nullable(),
  status: z.string().nullable(),
  internalEffectiveDate: z.string().nullable(),
  createdAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  relatedEntryId: z.string().nullable(),
  disputeId: z.string().nullable(),
  disputeDeadline: z.string().nullable(),
  isReversed: z.boolean(),
  billingCategory: BillingCategorySchema.nullable().optional(),
  adhocInvoiceId: AdHocInvoiceId.nullable().optional(),
  recurringFeeAssignmentId: RecurringFeeAssignmentId.nullable().optional(),
  source: z.string().nullable(),
  observedAt: z.string().nullable(),
  observedDateLabel: z.string().min(1),
  sourceKind: z.enum(['stripe-correlated', 'internal-ledger']),
  isStripeCorrelated: z.boolean(),
  stripeCorrelation: SchoolTuitionMirrorCorrelationSchema.nullable(),
});
export type SchoolTuitionTransaction = z.infer<typeof SchoolTuitionTransactionSchema>;

export const SchoolTuitionDueDateStateSchema = z.enum(['today', 'upcoming', 'overdue']).nullable();
export type SchoolTuitionDueDateState = z.infer<typeof SchoolTuitionDueDateStateSchema>;

export const SchoolTuitionStudentSummarySchema = z.object({
  id: StudentId,
  name: z.string().min(1),
  gradeLabel: z.string().min(1),
  divisionLabel: z.string().min(1),
  rateCategory: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});
export type SchoolTuitionStudentSummary = z.infer<typeof SchoolTuitionStudentSummarySchema>;

export const SchoolTuitionPayerSchema = z.object({
  id: z.string().min(1),
  holderType: z.enum(['family', 'parent', 'third_party']),
  displayName: z.string().min(1),
  relationLabel: z.string().min(1),
  email: z.string().nullable(),
  isPrimary: z.boolean(),
  allocationPercent: z.number(),
  allocationBalanceCents: z.number(),
  parentContactId: ParentContactId.nullable().optional(),
});
export type SchoolTuitionPayer = z.infer<typeof SchoolTuitionPayerSchema>;

export const SchoolTuitionDocumentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  fileName: z.string().nullable(),
  category: z.string().min(1),
  status: z.string().nullable(),
  uploadedAt: z.string().nullable(),
  uploadedAtLabel: z.string().min(1),
  studentId: StudentId.nullable(),
  studentName: z.string().nullable(),
  kind: z.enum(['receipt', 'contract', 'statement', 'document']),
  downloadMode: z.enum(['remote', 'derived', 'unavailable']),
  remoteDocumentId: z.string().nullable(),
  derivedTransactionId: z.string().nullable(),
});
export type SchoolTuitionDocument = z.infer<typeof SchoolTuitionDocumentSchema>;

export const SchoolTuitionFamilySchema = z.object({
  familyId: FamilyId,
  familyName: z.string().min(1),
  studentCount: z.number().int().nonnegative(),
  students: z.array(SchoolTuitionStudentSummarySchema).default([]),
  studentNames: z.array(z.string()).default([]),
  planLabel: z.string().nullable().optional(),
  hasTuitionBalanceSource: z.boolean().default(false),
  totalChargesCents: z.number(),
  totalCreditsCents: z.number(),
  netBalanceCents: z.number(),
  currentDueCents: z.number(),
  overdueCents: z.number(),
  autoPayEnrolled: z.boolean(),
  paymentMethodSummary: z.string().min(1),
  statusLabel: z.string().min(1),
  nextDueDate: z.string().nullable(),
  nextDueDateLabel: z.string().min(1),
  nextDueDateState: SchoolTuitionDueDateStateSchema,
  payers: z.array(SchoolTuitionPayerSchema).default([]),
  documents: z.array(SchoolTuitionDocumentSchema).default([]),
  atRisk: z.boolean(),
  transactions: z.array(SchoolTuitionTransactionSchema).default([]),
  hasCorrelatedStripeData: z.boolean(),
  latestObservedAt: z.string().nullable(),
});
export type SchoolTuitionFamily = z.infer<typeof SchoolTuitionFamilySchema>;

export const SchoolTuitionDashboardSummarySchema = z.object({
  totalBilledCents: z.number(),
  totalCollectedCents: z.number(),
  outstandingCents: z.number(),
  currentDueCents: z.number(),
  overdueCents: z.number(),
  autoPayFamilies: z.number().int().nonnegative(),
  collectionRate: z.number(),
  isDataEmpty: z.boolean(),
  emptyStateReason: z.enum(['no-family-accounts', 'no-tuition-activity', 'search-no-match']).nullable(),
});
export type SchoolTuitionDashboardSummary = z.infer<typeof SchoolTuitionDashboardSummarySchema>;

export const SchoolTuitionTrendDatumSchema = z.object({
  month: z.string().min(1),
  monthKey: z.string().regex(/^\d{4}-\d{2}$/),
  tuition: z.number(),
  fees: z.number(),
  due: z.number(),
  outstanding: z.number(),
  collected: z.number(),
  billed: z.number(),
  billedLine: z.number(),
  seriesMax: z.number(),
  dueCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int().nonnegative(),
  collectedCents: z.number().int(),
  billedCents: z.number().int().nonnegative(),
});
export type SchoolTuitionTrendDatum = z.infer<typeof SchoolTuitionTrendDatumSchema>;

export const SchoolTuitionDateRangeSchema = z.object({
  start: z.string().nullable(),
  end: z.string().nullable(),
});
export type SchoolTuitionDateRange = z.infer<typeof SchoolTuitionDateRangeSchema>;

export const SchoolTuitionDashboardTreeSchema = z.object({
  academicYear: AcademicYear,
  summary: SchoolTuitionDashboardSummarySchema,
  trend: z.array(SchoolTuitionTrendDatumSchema).default([]),
  families: z.array(SchoolTuitionFamilySchema).default([]),
  totalCount: z.number().int().nonnegative(),
  latestObservedAt: z.string().nullable(),
  hasCorrelatedStripeData: z.boolean(),
  dateRange: SchoolTuitionDateRangeSchema,
});
export type SchoolTuitionDashboardTree = z.infer<typeof SchoolTuitionDashboardTreeSchema>;

export const SchoolTuitionFamilyLedgerSummarySchema = z.object({
  totalChargesCents: z.number(),
  totalCreditsCents: z.number(),
  netBalanceCents: z.number(),
  currentDueCents: z.number(),
  overdueCents: z.number(),
  studentCount: z.number().int().nonnegative(),
  autoPayEnrolled: z.boolean(),
  paymentMethodSummary: z.string().min(1),
  statusLabel: z.string().min(1),
  nextDueDate: z.string().nullable(),
  nextDueDateLabel: z.string().min(1),
  nextDueDateState: SchoolTuitionDueDateStateSchema,
  atRisk: z.boolean(),
});
export type SchoolTuitionFamilyLedgerSummary = z.infer<typeof SchoolTuitionFamilyLedgerSummarySchema>;

export const SchoolTuitionFamilyLedgerTreeSchema = z.object({
  academicYear: AcademicYear,
  family: SchoolTuitionFamilySchema,
  ledgerEntries: z.array(SchoolTuitionTransactionSchema).default([]),
  payers: z.array(SchoolTuitionPayerSchema).default([]),
  documents: z.array(SchoolTuitionDocumentSchema).default([]),
  summary: SchoolTuitionFamilyLedgerSummarySchema,
  latestObservedAt: z.string().nullable(),
  hasCorrelatedStripeData: z.boolean(),
});
export type SchoolTuitionFamilyLedgerTree = z.infer<typeof SchoolTuitionFamilyLedgerTreeSchema>;
